"use client";

// What happens when a högtid particle is clicked: a short burst of pieces
// (crumbs, small hearts, petals, the flake blown away, …). Pure CSS animation
// of transform and opacity per piece, no JS loop; the burst removes itself
// after its longest piece has finished.

import type { CSSProperties, ReactNode } from "react";
import type { HogtidEffekt } from "@/lib/hogtider";
import { Partikel } from "./figurer";
import styles from "./hogtid.module.css";

type Bit = {
  dx: number;
  dy: number;
  rot: number;
  skala: number;
  ms: number;
  /** "gravitation": accelerates (crumbs falling); "ut": decelerates (bursts);
   *  "snurr": spins up, then shrinks away. */
  kurva: "gravitation" | "ut" | "snurr";
  innehall: ReactNode;
  storlek: number;
};

export interface Utbrott {
  id: number;
  x: number;
  y: number;
  bitar: Bit[];
  /** ms until every piece has finished */
  langd: number;
}

const slump = (a: number, b: number) => a + Math.random() * (b - a);

function prick(farg: string, d: number): ReactNode {
  return <span className={styles.prick} style={{ background: farg, width: d, height: d }} />;
}

function smulor(farger: string[], antal: number): Bit[] {
  return Array.from({ length: antal }, () => {
    const d = slump(2.5, 6);
    return {
      dx: slump(-45, 45),
      dy: slump(240, 420),
      rot: slump(-200, 200),
      skala: 1,
      ms: slump(600, 900),
      kurva: "gravitation" as const,
      innehall: prick(farger[Math.floor(Math.random() * farger.length)], d),
      storlek: d,
    };
  });
}

/** The pieces of one burst for a clicked particle of `storlek` px. */
export function skapaUtbrott(effekt: HogtidEffekt, x: number, y: number, storlek: number, farg: string): Utbrott {
  let bitar: Bit[] = [];
  switch (effekt) {
    case "sno": {
      // Blown away by a gust, then gone.
      const hall = Math.random() < 0.5 ? -1 : 1;
      bitar = [
        {
          dx: hall * slump(280, 460),
          dy: slump(-90, 10),
          rot: hall * slump(360, 720),
          skala: 0.8,
          ms: 1100,
          kurva: "ut",
          innehall: (
            <svg viewBox="0 0 24 24" width={storlek} height={storlek}>
              <Partikel effekt="sno" farg={farg} />
            </svg>
          ),
          storlek,
        },
      ];
      break;
    }
    case "hjartan":
      // Breaks into smaller hearts that drift apart and fade.
      bitar = Array.from({ length: 7 }, (_, i) => {
        const v = (i / 7) * Math.PI * 2 + slump(-0.3, 0.3);
        const r = slump(28, 55);
        const s = storlek * 0.45;
        return {
          dx: Math.cos(v) * r,
          dy: Math.sin(v) * r - 12,
          rot: slump(-30, 30),
          skala: 0.6,
          ms: slump(650, 900),
          kurva: "ut" as const,
          innehall: (
            <svg viewBox="0 0 24 24" width={s} height={s}>
              <Partikel effekt="hjartan" farg={farg} />
            </svg>
          ),
          storlek: s,
        };
      });
      break;
    case "semlor":
      bitar = smulor(["var(--color-figur-lera)", "var(--color-figur-havre)", "var(--color-figur-havre)", "var(--color-hogtid-choklad-ljus)"], 18);
      break;
    case "kanelbullar":
      bitar = smulor(["var(--color-figur-lera)", "var(--color-figur-lera)", "var(--color-figur-havre)", "var(--color-hogtid-pumpa-mork)"], 16);
      break;
    case "kladdkakor":
      bitar = smulor(["var(--color-hogtid-choklad)", "var(--color-hogtid-choklad-ljus)", "var(--color-figur-havre)"], 16);
      break;
    case "lussekatter":
      // Starts spinning, faster and faster, and spins away into nothing.
      bitar = [
        {
          dx: 0,
          dy: 0,
          rot: 1080,
          skala: 0.1,
          ms: 1100,
          kurva: "snurr",
          innehall: (
            <svg viewBox="0 0 24 24" width={storlek} height={storlek}>
              <Partikel effekt="lussekatter" farg="" />
            </svg>
          ),
          storlek,
        },
      ];
      break;
    case "blommor":
      // The petals come loose and flutter off.
      bitar = Array.from({ length: 6 }, (_, i) => {
        const v = (i / 6) * Math.PI * 2;
        return {
          dx: Math.cos(v) * slump(40, 70),
          dy: Math.sin(v) * slump(40, 70) + slump(30, 70),
          rot: slump(-260, 260),
          skala: 0.9,
          ms: slump(900, 1200),
          kurva: "ut" as const,
          innehall: <span className={styles.kronblad} style={{ background: farg, width: storlek * 0.24, height: storlek * 0.42 }} />,
          storlek: storlek * 0.42,
        };
      });
      break;
    case "pi":
      // The decimals appear and float off.
      bitar = [
        {
          dx: 0,
          dy: -70,
          rot: 0,
          skala: 1,
          ms: 1500,
          kurva: "ut",
          innehall: <span className={styles.decimaler}>3,14159265…</span>,
          storlek: 20,
        },
      ];
      break;
    case "flaggor":
      // Waves goodbye and flies up and away.
      bitar = [
        {
          dx: slump(-60, 60),
          dy: -360,
          rot: slump(-25, 25),
          skala: 1.15,
          ms: 1100,
          kurva: "ut",
          innehall: (
            <svg viewBox="0 0 24 24" width={storlek} height={storlek}>
              <Partikel effekt="flaggor" farg="" />
            </svg>
          ),
          storlek,
        },
      ];
      break;
    default:
      bitar = [];
  }
  return { id: performance.now() + Math.random(), x, y, bitar, langd: Math.max(0, ...bitar.map((b) => b.ms)) };
}

export function UtbrottVy({ utbrott }: { utbrott: Utbrott }) {
  return (
    <>
      {utbrott.bitar.map((b, i) => (
        <span
          key={i}
          className={`${styles.bit} ${b.kurva === "gravitation" ? styles.bitGravitation : b.kurva === "snurr" ? styles.bitSnurr : styles.bitUt}`}
          style={
            {
              left: utbrott.x - b.storlek / 2,
              top: utbrott.y - b.storlek / 2,
              "--dx": `${b.dx}px`,
              "--dy": `${b.dy}px`,
              "--rot": `${b.rot}deg`,
              "--skala": b.skala,
              animationDuration: `${b.ms}ms`,
            } as CSSProperties
          }
        >
          {b.innehall}
        </span>
      ))}
    </>
  );
}
